import type { FileAttachment } from '@/shared/types/projectData';
import i18n, { getCurrentLocale } from '@/shared/i18n/config';
import { parseServerDateTime } from '@/shared/utils/serverTime';

interface FileSizeFormatOptions {
  byteUnit?: string;
  precision?: number;
  zeroLabel?: string;
}

export const formatFileSizeLabel = (
  bytes: number,
  { byteUnit = 'B', precision = 1, zeroLabel = `0 ${byteUnit}` }: FileSizeFormatOptions = {}
): string => {
  if (bytes === 0) return zeroLabel;
  const k = 1024;
  const sizes = [byteUnit, 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Number((bytes / Math.pow(k, i)).toFixed(precision))} ${sizes[i]}`;
};

export const formatUploadTimestamp = (dateString: string, locale = getCurrentLocale()): string => {
  const date = parseServerDateTime(dateString);
  if (Number.isNaN(date.getTime())) {
    return i18n.t('Unknown time');
  }
  return date.toLocaleString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const concreteActorLabel = (value?: string): string | undefined => {
  const normalized = value?.trim();
  return normalized && normalized !== 'Unknown uploader' ? normalized : undefined;
};

export const uploadActorLabel = (file: Pick<FileAttachment, 'uploadedByActor' | 'uploadedByDisplay' | 'metadata' | 'uploadedBy'>): string => (
  concreteActorLabel(file.uploadedByActor?.displayName)
  || concreteActorLabel(file.uploadedByDisplay)
  || file.metadata.agentProfileName
  || file.metadata.uploadActorName
  || file.metadata.uploadActorUsername
  || file.uploadedBy
  || 'Unknown uploader'
);

export const qualityCheckLabel = (file: Pick<FileAttachment, 'qualityCheck' | 'metadata'>): string => {
  const status = file.qualityCheck?.status || file.metadata.qualityCheckStatus || 'not_required';
  if (status === 'pending_review') {
    return i18n.t('Quality check pending');
  }
  if (status === 'not_required') {
    return i18n.t('Quality check not required');
  }
  const label = file.qualityCheck?.label || status.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
  const reviewer = file.qualityCheck?.checkedBy?.name || file.metadata.qualityCheckedByName;
  const translatedStatus = i18n.t(label);
  const displayStatus = translatedStatus === label ? label.toLowerCase() : translatedStatus;
  if (reviewer) {
    return i18n.t('Quality {{status}} by {{reviewer}}', {
      status: displayStatus,
      reviewer,
    });
  }
  return i18n.t('Quality {{status}}', { status: displayStatus });
};
